import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import UserEmailBadge from '../components/UserEmailBadge'
import WeekSelector from '../components/WeekSelector'
import WeeklyMetricsForm from '../components/WeeklyMetricsForm'
import { formatDateToYMD, getWeekStartMonday, startOfDay } from '../utils/metricDateUtils'

const CustomerWeeklyDataEntryPage = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation(['dashboard', 'common'])

  const defaultWeekStart = useMemo(() => getWeekStartMonday(new Date()), [])

  const [selectedWeekStart, setSelectedWeekStart] = useState(defaultWeekStart)
  const [currentData, setCurrentData] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchDataForWeek()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeekStart, user])

  const handleLogout = async () => {
    const { error } = await signOut()
    if (error) console.error('Error signing out:', error)
    navigate('/login')
  }

  const fetchDataForWeek = async () => {
    if (!user || !selectedWeekStart) return

    try {
      setLoading(true)

      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_email', user.email)
        .single()

      if (businessError) {
        console.log('No business found for user')
        return
      }

      const weekKey = formatDateToYMD(startOfDay(selectedWeekStart))

      const { data: metrics, error: metricsError } = await supabase
        .from('weekly_metrics')
        .select('*')
        .eq('business_id', business.id)
        .eq('week_start_date', weekKey)
        .single()

      if (metricsError) {
        setCurrentData(null)
      } else {
        setCurrentData(metrics)
      }
    } catch (error) {
      console.error('Error fetching weekly data:', error)
      setCurrentData(null)
    } finally {
      setLoading(false)
    }
  }

  const handleWeekChange = (newWeekStart) => {
    setSelectedWeekStart(newWeekStart)
  }

  const handleDataSave = (savedData) => {
    setCurrentData(savedData)
  }

  const handleFirstMetricSaved = () => {
    navigate('/dashboard')
  }

  const formatWeekDisplay = (weekStart) => {
    const start = startOfDay(weekStart)
    const end = new Date(start)
    end.setDate(end.getDate() + 6)

    const locale = i18n.language === 'es' ? 'es-ES' : 'en-US'
    const startLabel = start.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric'
    })

    const endLabel = end.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })

    return `${startLabel} – ${endLabel}`
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-5 sm:py-6">
            <h1 className="text-3xl font-bold text-gray-900">
              <span>Interactive </span>
              <span className="text-[#FF1B54]">Business</span>
            </h1>

            <div className="flex items-center space-x-4">
              <UserEmailBadge email={user?.email} showProfileMenu />
              <button
                onClick={handleLogout}
                className="bg-[#FF1B54] hover:bg-[#E1174C] text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-2"
              >
                {t('navigation.logout', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-5 sm:px-0 sm:py-7">
          <div className="mb-7">
            <div className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              {t('customer.subtitle')}
            </div>
            <p className="mt-2 text-base leading-6 text-gray-600">
              {t('customer.weeklyDataEntrySubtitle')}
            </p>
          </div>
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex items-end gap-5 sm:gap-7">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-[#FF1B54] hover:bg-[#E1174C] text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-2"
              >
                {t('navigation.dashboard', { ns: 'common' })}
              </button>
              <span className="py-2.5 px-1 border-b-2 border-[#FF1B54] font-semibold text-sm text-[#FF1B54]">
                {t('customer.weeklyMetrics')}
              </span>
            </nav>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(340px,0.9fr)_minmax(0,2.1fr)] gap-5 xl:gap-6 items-start">
              <div>
                <WeekSelector
                  selectedWeekStart={selectedWeekStart}
                  onWeekChange={handleWeekChange}
                />

                {currentData && (
                  <div className="mt-5 bg-white border border-gray-200 shadow-sm rounded-xl p-5">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      {t('customer.currentDataFor', { period: formatWeekDisplay(selectedWeekStart) })}
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dataEntry.fields.revenue')}:</span>
                        <span className="font-semibold text-gray-900">
                          ${currentData.revenue?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dataEntry.fields.expenses')}:</span>
                        <span className="font-semibold text-gray-900">
                          ${currentData.expenses?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dataEntry.fields.leads')}:</span>
                        <span className="font-semibold text-gray-900">
                          {currentData.leads?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dataEntry.fields.customers')}:</span>
                        <span className="font-semibold text-gray-900">
                          {currentData.customers?.toLocaleString() || '0'}
                        </span>
                      </div>
                      {/* Social Following (temporarily removed) */}
                      {/**
                       * TODO: Re-enable when we decide how/where it should be used.
                       */}
                      {/*
                      <div className="flex justify-between">
                        <span className="text-gray-600">Social Following:</span>
                        <span className="font-medium">
                          {currentData.social_following?.toLocaleString() || '0'}
                        </span>
                      </div>
                      */}
                    </div>
                  </div>
                )}
              </div>

              <div className="min-w-0">
                {loading ? (
                  <div className="bg-white border border-gray-200 border-t-[3px] border-t-[#FF1B54] shadow-sm rounded-2xl p-6 sm:p-8">
                    <div className="flex justify-center items-center h-64">
                      <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-gray-200 border-b-[#FF1B54]"></div>
                    </div>
                  </div>
                ) : (
                  <WeeklyMetricsForm
                    selectedWeekStart={selectedWeekStart}
                    existingData={currentData}
                    onSave={handleDataSave}
                    onDataChange={fetchDataForWeek}
                    onFirstMetricSaved={handleFirstMetricSaved}
                  />
                )}
              </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default CustomerWeeklyDataEntryPage
